import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  History, 
  Settings, 
  LogOut,
  Menu,
  X,
  BookOpen,
  ClipboardList,
  FolderOpen,
  Clock,
  AlertTriangle,
  CreditCard,
  UserCircle
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";

export const Layout = ({ children }) => {
  const { user, logout, hasMaterialsAccess, hasExamsAccess, hasClientsAccess, isAccessExpired, getDaysRemaining } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const daysRemaining = getDaysRemaining();
  const accessExpired = isAccessExpired();
  
  // Build navigation items based on permissions
  const buildStudentNavItems = () => {
    const items = [
      { href: "/dashboard", icon: LayoutDashboard, label: t("dashboard"), shortLabel: "Início" },
    ];
    
    // Add materials if user has access (base, complete)
    if (hasMaterialsAccess()) {
      items.push({ href: "/materials", icon: FolderOpen, label: t("materials"), shortLabel: "Materiais" });
    }
    
    // Add exams if user has access (base, complete)
    if (hasExamsAccess()) {
      items.push({ href: "/exams", icon: BookOpen, label: t("exams"), shortLabel: "Simulados" });
      items.push({ href: "/history", icon: History, label: t("history"), shortLabel: "Histórico" });
    }
    
    // Add clients if user has access (advanced, complete)
    if (hasClientsAccess()) {
      items.push({ href: "/clients", icon: Users, label: t("clients"), shortLabel: "Clientes" });
    }
    
    return items;
  };

  const adminNavItems = [
    { href: "/admin", icon: LayoutDashboard, label: t("dashboard"), shortLabel: "Início" },
    { href: "/admin/users", icon: Users, label: t("users"), shortLabel: "Usuários" },
    { href: "/admin/subscriptions", icon: CreditCard, label: "Assinaturas", shortLabel: "Assin." },
    { href: "/admin/exams", icon: BookOpen, label: t("exams"), shortLabel: "Simulados" },
    { href: "/admin/templates", icon: ClipboardList, label: t("templates"), shortLabel: "Templates" },
    { href: "/admin/materials", icon: FolderOpen, label: t("materials"), shortLabel: "Materiais" },
  ];
  
  const navItems = user?.role === "admin" ? adminNavItems : buildStudentNavItems();
  
  const handleLogout = () => {
    logout();
    navigate("/");
  };
  
  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.href;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.href}
        onClick={() => mobile && setMobileOpen(false)}
        className={`
          flex items-center gap-3 px-4 py-3 transition-colors
          ${mobile ? "text-2xl font-serif py-4" : "text-sm"}
          ${isActive ? "sidebar-active font-semibold" : "text-muted-foreground hover:text-foreground"}
        `}
      >
        <Icon size={mobile ? 28 : 20} strokeWidth={1.5} />
        <span>{item.label}</span>
      </Link>
    );
  };
  
  return (
    <div className="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="dashboard-sidebar" data-testid="desktop-sidebar">
        <div className="p-6 border-b">
          <Link to={user?.role === "admin" ? "/admin" : "/dashboard"}>
            <img src={LOGO_URL} alt="AESTHA" className="h-14 invert" />
          </Link>
        </div>
        
        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </nav>
        
        <div className="p-4 border-t">
          {/* Days remaining indicator for students */}
          {user?.role !== "admin" && daysRemaining !== null && (
            <div className={`mb-3 p-2 rounded text-xs font-mono ${
              daysRemaining <= 0 
                ? "bg-red-50 text-red-700 border border-red-200" 
                : daysRemaining <= 7 
                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                  : "bg-green-50 text-green-700 border border-green-200"
            }`}>
              <div className="flex items-center gap-1">
                <Clock size={12} />
                {daysRemaining <= 0 ? (
                  <span>Acesso expirado</span>
                ) : (
                  <span>{daysRemaining} {daysRemaining === 1 ? "dia" : "dias"} restantes</span>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {user?.name}
            </div>
            <LanguageSelector variant="nav" />
          </div>
          {/* My Account Link */}
          <Link
            to="/my-account"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
            data-testid="my-account-link"
          >
            <UserCircle size={18} strokeWidth={1.5} />
            <span>Minha Conta</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="logout-button"
          >
            <LogOut size={18} strokeWidth={1.5} />
            <span>{t("logout")}</span>
          </button>
        </div>
      </aside>
      
      {/* Access Expired Banner */}
      {user?.role !== "admin" && accessExpired && (
        <div className="fixed top-0 left-0 right-0 z-50 md:left-[280px] bg-red-600 text-white px-4 py-3 flex items-center justify-center gap-2 h-12" data-testid="expired-banner">
          <AlertTriangle size={18} />
          <span className="text-sm font-medium">
            Seu acesso expirou. Renove em Minha Conta.
          </span>
        </div>
      )}
      
      {/* Mobile Header */}
      <header className={`md:hidden fixed left-0 right-0 h-16 bg-white border-b z-40 flex items-center justify-between px-4 ${accessExpired && user?.role !== "admin" ? "top-12" : "top-0"}`}>
        <Link to={user?.role === "admin" ? "/admin" : "/dashboard"}>
          <img src={LOGO_URL} alt="AESTHA" className="h-12 invert" />
        </Link>
        
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu size={24} strokeWidth={1.5} />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full bg-white p-0">
            <div className="flex flex-col h-full">
              <div className="p-6 border-b flex items-center">
                <img src={LOGO_URL} alt="AESTHA" className="h-14 invert" />
              </div>
              
              <nav className="flex-1 py-8 px-2">
                {navItems.map((item) => (
                  <NavLink key={item.href} item={item} mobile />
                ))}
              </nav>
              
              <div className="p-6 border-t">
                {/* Days remaining indicator for students (mobile) */}
                {user?.role !== "admin" && daysRemaining !== null && (
                  <div className={`mb-4 p-3 rounded text-sm ${
                    daysRemaining <= 0 
                      ? "bg-red-50 text-red-700 border border-red-200" 
                      : daysRemaining <= 7 
                        ? "bg-orange-50 text-orange-700 border border-orange-200"
                        : "bg-green-50 text-green-700 border border-green-200"
                  }`}>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      {daysRemaining <= 0 ? (
                        <span>Acesso expirado</span>
                      ) : (
                        <span>{daysRemaining} {daysRemaining === 1 ? "dia" : "dias"} restantes</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
                    {user?.name}
                  </div>
                  <LanguageSelector variant="nav" />
                </div>
                {/* My Account Link (mobile) */}
                <Link
                  to="/my-account"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 text-lg text-muted-foreground hover:text-foreground transition-colors mb-4"
                  data-testid="my-account-link-mobile"
                >
                  <UserCircle size={24} strokeWidth={1.5} />
                  <span>Minha Conta</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 text-lg text-muted-foreground hover:text-foreground transition-colors"
                >
                  <LogOut size={24} strokeWidth={1.5} />
                  <span>{t("logout")}</span>
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>
      
      {/* Main Content */}
      <main className={`dashboard-content pb-20 md:pb-0 ${accessExpired && user?.role !== "admin" ? "pt-28 md:pt-12" : "pt-16 md:pt-0"}`} data-testid="main-content">
        <div className="animate-fadeIn">
          {children}
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
        {navItems.slice(0, 4).map((item) => {
          const isActive = location.pathname === item.href;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`mobile-nav-item ${isActive ? "active" : ""}`}
            >
              <Icon size={22} strokeWidth={1.5} />
              <span>{item.shortLabel || item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
